import { forwardRef, MouseEvent } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';

export type Direction = 'left' | 'right';

interface Props {
  absolute?: boolean;
  buttonStyles?: SerializedStyles;
  className?: string;
  direction: Direction;
  disabled?: boolean;
  isVisible?: boolean;
  onClick: (arg: Direction, e: MouseEvent) => void;
  trackingId?: string;
}

export const ScrollControl = forwardRef<HTMLButtonElement, Props>(
  (
    { absolute, buttonStyles, className = '', direction, disabled = false, isVisible = true, onClick, trackingId },
    ref
  ) => {
    const { t } = useTranslation('analytics');
    const isLeftScroll = direction === 'left';
    const { isSmallMode } = useIsSmallMode();

    return isVisible ? (
      <Button
        className={`${isLeftScroll ? 'left-scroll' : 'right-scroll'} ${className} no-pdf ${
          absolute ? 'absolute' : ''
        } ${isSmallMode ? 'small-mode' : ''}`.trim()}
        css={[styles, buttonStyles]}
        disabled={disabled}
        label={isLeftScroll ? t('Scroll left') : t('Scroll right')}
        onClick={(e) => onClick(direction, e)}
        ref={ref}
        trackingId={trackingId}
        variant='secondary'
      >
        {isLeftScroll ? <Icon name='caret-left' /> : <Icon name='caret-right' />}
      </Button>
    ) : null;
  }
);

ScrollControl.displayName = 'ScrollControl';

const styles = css`
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.light};
  cursor: pointer;
  min-width: auto;
  width: ${theme.spacing(4)};

  &.absolute {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  &.left-scroll.absolute {
    left: ${theme.spacing(-2)};

    &.small-mode {
      left: ${theme.spacing(-1)};
    }
  }

  &.right-scroll.absolute {
    right: ${theme.spacing(-2)};

    &.small-mode {
      right: ${theme.spacing(-1)};
    }
  }
`;
